import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signalsApi, type Signal } from '~/lib/api';

type PositionStatus = 'watching' | 'open' | 'partial' | 'closed' | 'dismissed' | 'expired';

interface EditSignalModalProps {
  signal: Signal;
  positionId: string;
  positionStatus: string;
  positionEntryPrice?: number | null;
  onClose: () => void;
}

export function EditSignalModal({ signal, positionId, positionStatus, positionEntryPrice, onClose }: EditSignalModalProps) {
  const queryClient = useQueryClient();

  const [ticker, setTicker] = useState(signal.ticker);
  const [strike, setStrike] = useState(signal.strike.toString());
  const [optionType, setOptionType] = useState<'C' | 'P'>(signal.option_type);
  const [expiry, setExpiry] = useState(signal.expiry || '');

  // Use position's entry price if available (it may differ from signal price due to price_update corruption)
  const effectiveEntryPrice = positionEntryPrice ?? signal.signal_price;
  const [signalPrice, setSignalPrice] = useState(effectiveEntryPrice.toString());
  const [status, setStatus] = useState<PositionStatus>(positionStatus as PositionStatus);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const editMutation = useMutation({
    mutationFn: (data: Parameters<typeof signalsApi.edit>[1]) =>
      signalsApi.edit(signal.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signals', 'threads'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['thread', signal.position_id], refetchType: 'all' });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update signal');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => signalsApi.deletePosition(positionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signals', 'threads'], refetchType: 'all' });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to delete trade');
      setConfirmDelete(false);
    },
  });

  const isPending = editMutation.isPending || deleteMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const updates: Parameters<typeof signalsApi.edit>[1] = {};

    if (ticker !== signal.ticker) updates.ticker = ticker;
    if (parseFloat(strike) !== signal.strike) updates.strike = parseFloat(strike);
    if (optionType !== signal.option_type) updates.option_type = optionType;
    if (expiry !== (signal.expiry || '')) updates.expiry = expiry || null;

    if (parseFloat(signalPrice) !== effectiveEntryPrice) updates.signal_price = parseFloat(signalPrice);
    if (status !== positionStatus) updates.status = status;

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    editMutation.mutate(updates);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmDelete) {
          setConfirmDelete(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, confirmDelete]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center glass-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-modal p-6 w-full h-full overflow-y-auto sm:h-auto sm:max-w-md sm:mx-4 sm:max-h-[85vh] sm:rounded-xl rounded-none">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Edit Signal</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Ticker */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
              Ticker
            </label>
            <input
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              className="input w-full"
              required
            />
          </div>

          {/* Strike and Option Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
                Strike
              </label>
              <input
                type="number"
                step="0.5"
                value={strike}
                onChange={(e) => setStrike(e.target.value)}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
                Type
              </label>
              <select
                value={optionType}
                onChange={(e) => setOptionType(e.target.value as 'C' | 'P')}
                className="input w-full"
              >
                <option value="C">Call (C)</option>
                <option value="P">Put (P)</option>
              </select>
            </div>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
              Expiry Date
            </label>
            <input
              type="text"
              placeholder="MM/DD or empty"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="input w-full"
            />
          </div>

          {/* Entry Price */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
              Entry Price
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={signalPrice}
              onChange={(e) => setSignalPrice(e.target.value)}
              className="input w-full"
              required
            />
          </div>

          {/* Position Status */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as PositionStatus)}
              className="input w-full"
            >
              <option value="watching">Watch</option>
              <option value="open">Open</option>
              <option value="partial">Trim</option>
              <option value="closed">Closed</option>
              <option value="dismissed">Dismissed</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {error && (
            <div className="text-loss text-sm">{error}</div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-2">
            <button
              type="button"
              onClick={() => confirmDelete ? deleteMutation.mutate() : setConfirmDelete(true)}
              className="text-sm text-loss/60 hover:text-loss transition-colors"
              disabled={isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : confirmDelete ? 'Yes, delete' : 'Delete trade'}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => confirmDelete ? setConfirmDelete(false) : onClose()}
                className="btn btn-secondary"
                disabled={isPending}
              >
                {confirmDelete ? 'No, keep it' : 'Cancel'}
              </button>
              {!confirmDelete && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isPending}
                >
                  {editMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
